import { Fragment, useMemo, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import { CalendarDaysIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

export interface DateRange {
  from: string; // ISO 8601
  to: string;   // ISO 8601 (exclusive)
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (next: DateRange) => void;
  maxDays?: number;
}

interface Preset {
  key: string;
  label: string;
  build: () => DateRange;
}

const MS_DAY = 86_400_000;

function isoStartOfDay(d: Date): string {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
}
function isoEndOfDay(d: Date): string {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
}

export function buildDefaultPresets(maxDays = 365): Preset[] {
  const now = new Date();
  return [
    {
      key: 'last_7d',
      label: 'Last 7 days',
      build: () => ({
        from: new Date(now.getTime() - 7 * MS_DAY).toISOString(),
        to: now.toISOString(),
      }),
    },
    {
      key: 'last_30d',
      label: 'Last 30 days',
      build: () => ({
        from: new Date(now.getTime() - 30 * MS_DAY).toISOString(),
        to: now.toISOString(),
      }),
    },
    {
      key: 'last_90d',
      label: 'Last 90 days',
      build: () => ({
        from: new Date(now.getTime() - 90 * MS_DAY).toISOString(),
        to: now.toISOString(),
      }),
    },
    {
      key: 'mtd',
      label: 'Month to date',
      build: () => ({
        from: isoStartOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: now.toISOString(),
      }),
    },
    {
      key: 'qtd',
      label: 'Quarter to date',
      build: () => {
        const q = Math.floor(now.getMonth() / 3) * 3;
        return {
          from: isoStartOfDay(new Date(now.getFullYear(), q, 1)),
          to: now.toISOString(),
        };
      },
    },
    {
      key: 'ytd',
      label: 'Year to date',
      build: () => ({
        from: isoStartOfDay(new Date(now.getFullYear(), 0, 1)),
        to: now.toISOString(),
      }),
    },
    {
      key: 'last_365d',
      label: `Last ${maxDays} days`,
      build: () => ({
        from: new Date(now.getTime() - maxDays * MS_DAY).toISOString(),
        to: now.toISOString(),
      }),
    },
  ];
}

function formatLabel(value: DateRange): string {
  try {
    const f = new Date(value.from);
    const t = new Date(value.to);
    const fmt = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
    return `${fmt.format(f)} – ${fmt.format(t)}`;
  } catch {
    return `${value.from} – ${value.to}`;
  }
}

export default function DateRangePicker({ value, onChange, maxDays = 365 }: DateRangePickerProps) {
  const presets = useMemo(() => buildDefaultPresets(maxDays), [maxDays]);
  const [customFrom, setCustomFrom] = useState(() => value.from.slice(0, 10));
  const [customTo, setCustomTo] = useState(() => value.to.slice(0, 10));
  const [error, setError] = useState<string | null>(null);

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <Popover.Button className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50">
            <CalendarDaysIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
            {formatLabel(value)}
            <ChevronDownIcon className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute right-0 z-10 mt-2 w-80 rounded-md bg-white p-3 shadow-lg ring-1 ring-black ring-opacity-5">
              <div className="grid grid-cols-2 gap-2">
                {presets.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => {
                      onChange(preset.build());
                      close();
                    }}
                    className="rounded-md px-2 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              <div className="mt-3 border-t border-gray-200 pt-3">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Custom</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs text-gray-600">
                    From
                    <input
                      type="date"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 text-xs"
                    />
                  </label>
                  <label className="block text-xs text-gray-600">
                    To
                    <input
                      type="date"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 text-xs"
                    />
                  </label>
                </div>
                {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
                <button
                  type="button"
                  onClick={() => {
                    if (!customFrom || !customTo) {
                      setError('Pick both dates');
                      return;
                    }
                    const f = new Date(customFrom);
                    const t = new Date(customTo);
                    if (isNaN(f.getTime()) || isNaN(t.getTime())) {
                      setError('Invalid dates');
                      return;
                    }
                    if (f >= t) {
                      setError('"From" must be before "To"');
                      return;
                    }
                    if ((t.getTime() - f.getTime()) / MS_DAY > maxDays) {
                      setError(`Range must be ${maxDays} days or fewer`);
                      return;
                    }
                    setError(null);
                    onChange({ from: isoStartOfDay(f), to: isoEndOfDay(t) });
                    close();
                  }}
                  className="mt-2 w-full rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  Apply
                </button>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
